/**
 * Contact BFF commands (load, updates, scheduling, tags) return a payload shaped
 * like `ContactView.build`: `{ contact: { ... } }`. The contact detail UI keeps
 * the inner object as `contactData`.
 */

export const UNNAMED_CONTACT_LABEL = "Unnamed Contact"

function trimStr(value: unknown): string {
  if (value == null) return ""
  return String(value).trim()
}

/** True when contact has no real stored name (includes legacy DB placeholder). */
export function isUnnamedContact(record: Record<string, unknown> | null | undefined): boolean {
  if (!record) return false
  if (record.is_unnamed === true) return true
  const first = trimStr(record.first_name)
  const last = trimStr(record.last_name)
  if (first === UNNAMED_CONTACT_LABEL && !last) return true
  const display = trimStr(record.name) || trimStr(record.display_name)
  const full = trimStr(record.full_name)
  if (display && display !== UNNAMED_CONTACT_LABEL) return false
  if (full && full !== UNNAMED_CONTACT_LABEL) return false
  if (first || last) return false
  if (display === UNNAMED_CONTACT_LABEL || full === UNNAMED_CONTACT_LABEL) return true
  return !(display || full || first || last)
}

/** List/table cell: plain label when unnamed (no italics). */
export function contactListDisplayName(row: Record<string, unknown>): string {
  if (isUnnamedContact(row)) return UNNAMED_CONTACT_LABEL
  const name = trimStr(row.name)
  if (name) return name
  const first = trimStr(row.first_name)
  const last = trimStr(row.last_name)
  const combined = [first, last].filter(Boolean).join(" ")
  return combined || UNNAMED_CONTACT_LABEL
}

/** Detail header: italic when showing the placeholder label. */
export function contactPageDisplayName(record: Record<string, unknown> | null | undefined): {
  text: string
  isPlaceholder: boolean
} {
  if (!record || isUnnamedContact(record)) {
    return { text: UNNAMED_CONTACT_LABEL, isPlaceholder: true }
  }
  const name = trimStr(record.name)
  if (name) return { text: name, isPlaceholder: false }
  const first = trimStr(record.first_name)
  const last = trimStr(record.last_name)
  const combined = [first, last].filter(Boolean).join(" ")
  if (combined) return { text: combined, isPlaceholder: false }
  return { text: UNNAMED_CONTACT_LABEL, isPlaceholder: true }
}

/** Tab title for a loaded contact. */
export function contactDocumentTitle(
  contact: Record<string, unknown> | null
): string {
  if (!contact) return "Contact"
  return contactPageDisplayName(contact).text
}

export function contactRecordFromCommandData(
  data: unknown
): Record<string, unknown> | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null
  const d = data as Record<string, unknown>
  const inner = d.contact
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return null
}

/**
 * Apply a successful command `data` envelope via `onUpdate`. Returns false if
 * the payload cannot be normalized (caller should surface an error).
 */
export function applyContactViewResponse(
  data: unknown,
  onUpdate: (contact: Record<string, unknown>) => void
): boolean {
  const next = contactRecordFromCommandData(data)
  if (!next) return false
  onUpdate(next)
  return true
}
